// The way back to a client, on every page reached from one:
//   Clients › Shah, Pahel  G079 › Documents › GAPP Submissions › Family forms
// Each step is a link to the exact tab it names (the profile keeps its tab in
// the URL), and the bar stays at the top of the page as it scrolls. Before it,
// a page opened from a client — a signature request, the client edit form, an
// assessment, Prior Auth — had no way back short of searching for the client
// again (raised 2026-10-05).
//
// Give it the client row when the page has one; otherwise its id (uuid or
// G-number) and it fetches the name itself. A page about no client (a
// signature request to an agency) passes `root` — its own first steps — instead.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export const TRAIL_HEIGHT = 40;    // the client profile pins its tabs just below the bar (index.css)

// Where each profile tab and sub-tab lives — one place, so every trail says
// the same thing and lands on the same screen.
const TAB_LABEL = { overview: 'Overview', clinical: 'Clinical', documents: 'Documents', calendar: 'Calendar', schedule: 'Schedule', verification: 'Visit Verification', dme: 'DME', activity: 'Activity Log', tasks: 'Tasks', qm: 'Quality Mgmt' };
const SUB_LABEL = {
  documents: { files: 'Files', submissions: 'GAPP Submissions', esign: 'e-Signatures', faxes: 'Faxes', docreview: 'Document Review' },
  clinical: { summary: 'Summary', assessments: 'Assessments', careplan: 'Care Plan & ADLs', supervision: 'Supervision', mar: 'MAR', reports: 'Caregiver Reports' },
  qm: { surveys: 'Surveys', complaints: 'Complaint Log', incidents: 'Incident Log', reports: 'Reports' },
};
export const clientTabPath = (clientId, tab, sub) => {
  const q = new URLSearchParams();
  if (tab && tab !== 'overview') q.set('tab', tab);
  if (sub) q.set('sub', sub);
  return `/clients/${clientId}${q.toString() ? `?${q}` : ''}`;
};
// The crumbs for a profile tab (and sub-tab), linked.
export const tabCrumbs = (clientId, tab, sub) => [
  ...(tab && tab !== 'overview' ? [{ label: TAB_LABEL[tab] || tab, to: clientTabPath(clientId, tab) }] : []),
  ...(tab && sub && SUB_LABEL[tab]?.[sub] ? [{ label: SUB_LABEL[tab][sub], to: clientTabPath(clientId, tab, sub) }] : []),
];

const cache = new Map();
const isUuid = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v || ''));
async function loadClient(key) {
  if (cache.has(key)) return cache.get(key);
  const q = supabase.from('clients').select('id, client_id, first_name, last_name');
  const { data } = await (isUuid(key) ? q.eq('id', key) : q.eq('client_id', key)).maybeSingle();
  if (data) cache.set(key, data);
  return data || null;
}

export default function ClientTrail({ client, clientId, crumbs = [], right = null, sticky = true, root = null }) {
  const [c, setC] = useState(client || null);
  useEffect(() => {
    if (client) { setC(client); return undefined; }
    if (!clientId) return undefined;
    let live = true;
    loadClient(clientId).then((row) => { if (live) setC(row); });
    return () => { live = false; };
  }, [client, clientId]);

  const steps = root ? [...root, ...crumbs] : [
    { label: 'Clients', to: '/clients' },
    ...(c ? [{ label: c.last_name ? `${c.last_name}, ${c.first_name}` : c.first_name, code: c.client_id, to: `/clients/${c.id}` }] : clientId ? [{ label: '…' }] : []),
    ...crumbs,
  ];
  return (
    <nav aria-label="Breadcrumb" className={sticky ? 'client-trail-pinned' : undefined} style={{
      ...(sticky ? { position: 'sticky', zIndex: 30 } : {}),
      background: 'var(--bg)', minHeight: TRAIL_HEIGHT, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, borderBottom: '1px solid var(--border)',
    }}>
      <ol style={{ display: 'flex', alignItems: 'center', gap: 6, listStyle: 'none', margin: 0, padding: '8px 0', fontSize: 12.5, minWidth: 0, flex: 1, overflowX: 'auto', whiteSpace: 'nowrap' }}>
        {steps.map((s, i) => {
          const last = i === steps.length - 1;
          return (
            <li key={`${i}-${s.label}`} style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {i > 0 && <span aria-hidden="true" style={{ color: 'var(--text3)' }}>›</span>}
              {s.to && !last
                ? <Link to={s.to} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: s.code ? 600 : 500 }}>{s.label}{s.code && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>{s.code}</span>}</Link>
                : <span aria-current={last ? 'page' : undefined} style={{ color: last ? 'var(--text)' : 'var(--text2)', fontWeight: last ? 600 : 500 }}>{s.label}{s.code && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>{s.code}</span>}</span>}
            </li>
          );
        })}
      </ol>
      {right}
    </nav>
  );
}
